import { all, call, delay, getContext, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import type { Action } from '../utils';
import type { IFetchWebNotificationsRequest, INotificationRequest, IWebNotification } from '../../api/domain';
import type { NotificationsApiService } from '../../api/services/notifications/notifications-api-service';
import { hideProgressIndicator, showProgressIndicator } from '../application/application-actions';
import { logError, logInfo } from '../logging-api/logging-api-actions';
import {
    fetchWebNotifications,
    fetchWebNotificationsDebounced,
    fetchWebNotificationsFailure,
    fetchWebNotificationsSuccess,
    sendNotification,
    sendNotificationFailure,
    sendNotificationSuccess,
} from './notifications-api-actions';

function* handleFetchWebNotifications(action: Action<IFetchWebNotificationsRequest>) {
    const api: NotificationsApiService = yield getContext('notificationsApiService');
    try {
        const response: { notifications: IWebNotification[]; totalCount: number } = yield call([api, api.fetchWebNotifications], action.payload);
        yield put(fetchWebNotificationsSuccess(response));
    } catch (e: unknown) {
        yield put(fetchWebNotificationsFailure());
        yield put(logError(`handleFetchWebNotifications failed with: ${e}`));
    }
}

function* handleFetchWebNotificationsDebounced(action: Action<{ request: IFetchWebNotificationsRequest; delayMS: number }>) {
    const {
        payload: { request, delayMS },
    } = action;
    yield delay(delayMS);
    yield put(showProgressIndicator());
    try {
        yield put(fetchWebNotifications(request));
        yield take([fetchWebNotificationsSuccess, fetchWebNotificationsFailure]);
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleSendNotification(action: Action<INotificationRequest>) {
    try {
        const api: NotificationsApiService = yield getContext('notificationsApiService');
        const location: string = yield call([api, api.sendNotification], action.payload);
        yield put(sendNotificationSuccess(location));
    } catch (e: unknown) {
        yield put(sendNotificationFailure());
        yield put(logError(`handleSendNotification failed with: ${e}`));
    }
}

function* handleSendNotificationSuccess(action: Action<INotificationRequest>) {
    yield put(logInfo(`new notification registered from ${action.payload.producerLoginId} to ${action.payload.recipientLoginId}`));
}

export function* notificationsApiSaga() {
    yield all([
        takeEvery(fetchWebNotifications, handleFetchWebNotifications),
        takeLatest(fetchWebNotificationsDebounced, handleFetchWebNotificationsDebounced),
        takeEvery(sendNotification, handleSendNotification),
        takeEvery(sendNotification, handleSendNotificationSuccess),
    ]);
}
